import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';

import { StepperAccordionSummary } from '@klareospace/pharos-lib-web-utils';
import type { StepperAccordionSummaryItem } from '@klareospace/pharos-lib-web-utils';

import { formatRouteAddress, resolveDeliveryContact } from '../utils';
import type { OrderCreationFormInput } from '../schemas/order-creation.schema';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Collapsed "Route information" section recap. Owns its own `useWatch` on `routeInformation` (and
 * the selected customer, for the contact fallback) so a Route information keystroke only re-renders
 * this leaf instead of the whole drawer.
 */
export function RouteInformationSectionSummary(): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('order-creation');
  const { control } = useFormContext<OrderCreationFormInput>();
  const { deliveryAddress, selectedSite } = useWatch({ control, name: 'routeInformation' });
  const selectedCustomer = useWatch({ control, name: 'customer.selectedCustomer' });

  const contact = deliveryAddress ? resolveDeliveryContact(deliveryAddress, selectedCustomer) : null;

  // Kept as a closure (not a top-level helper) so the i18next-no-undefined-translation-keys
  // ESLint rule can resolve `t`'s namespace from the enclosing `useTranslation` call.
  function buildRouteInformationSummaryItems(): StepperAccordionSummaryItem[] {
    const items: StepperAccordionSummaryItem[] = [];
    if (selectedSite) {
      items.push({ label: t('common.pickupTitle'), value: selectedSite.name });
    }
    if (deliveryAddress) {
      items.push({
        label: tCommon('fields.delivery'),
        value: formatRouteAddress(deliveryAddress.address),
      });
    }
    if (contact?.name) {
      items.push({ label: tCommon('fields.contactSectionTitle'), value: contact.name });
    }
    if (contact?.phone) {
      items.push({ label: tCommon('fields.phone'), value: contact.phone });
    }
    return items;
  }

  return <StepperAccordionSummary items={buildRouteInformationSummaryItems()} />;
}
